import React, { useState } from 'react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import { submitFeedback, retractFeedback } from 'features/evals3/api/evals3Api';

/**
 * "Was this eval right?" on a v3 verdict (TRO-415).
 *
 * Duplicated from the current evals' control on purpose: the two systems
 * share no code, so deleting the old one is a file delete. Behaviour is the
 * same. NOT a human eval node — a human node grades the agent; this grades
 * the CHECK. Two glyphs until used; a thumbs-down opens one inline form,
 * because a down-vote without a corrected verdict and a reason compiles into
 * nothing and is refused by the API and a CHECK constraint.
 *
 * Up is a toggle (clicking the filled thumb retracts); retracting a
 * correction takes the explicit "Remove" in the open form.
 */
export default function Eval3VerdictFeedback({ teamId, verdictId, value, onChange, onMarkedWrong = null, onReview = null }) {
  const [open, setOpen] = useState(false);
  const [correctedPassed, setCorrectedPassed] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const thumbs = value?.thumbs ?? null;
  if (!teamId || verdictId == null) return null;

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  async function send(body) {
    setBusy(true); setError(null);
    try {
      const saved = await submitFeedback(teamId, { eval_run_id: verdictId, ...body });
      onChange?.(saved);
      setOpen(false);
      // A result marked wrong is a question for Ask: the page opens it beside itself.
      if (body.thumbs === 'down' && saved && onMarkedWrong) onMarkedWrong(saved);
      return saved;
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not save');
      return null;
    } finally { setBusy(false); }
  }

  async function retract() {
    setBusy(true);
    try { await retractFeedback(teamId, value.id); onChange?.(null); setOpen(false); }
    catch (err) { setError(err?.response?.data?.message || err.message); }
    finally { setBusy(false); }
  }

  async function clickUp(e) {
    stop(e);
    if (thumbs === 'up' && value?.id) { await retract(); return; }
    await send({ thumbs: 'up' });
  }

  function clickDown(e) {
    stop(e);
    // Where the steps are at hand (the results page), a thumbs-down is a step
    // review, not a two-field form.
    if (onReview) { onReview(verdictId); return; }
    setCorrectedPassed(value?.corrected_passed ?? null);
    setReason(value?.reason_text || '');
    setError(null);
    setOpen((v) => !v);
  }

  async function saveDown(e) {
    stop(e);
    if (typeof correctedPassed !== 'boolean') { setError('Pick Pass or Fail.'); return; }
    if (!reason.trim()) { setError('Say why.'); return; }
    await send({ thumbs: 'down', corrected_passed: correctedPassed, reason_text: reason.trim() });
  }

  const btn = (active) => [
    'rounded px-1 py-0.5 text-[12px] leading-none transition-colors disabled:opacity-40',
    active ? 'bg-brand-soft' : 'hover:bg-surface-hover',
  ].join(' ');

  return (
    <div className="relative inline-flex flex-col items-start" onClick={stop} role="presentation">
      <div className="inline-flex items-center gap-0.5">
        <button type="button" disabled={busy} onClick={clickUp} className={btn(thumbs === 'up')}
          title={thumbs === 'up' ? 'Marked right. Click to undo' : 'Right'}
          aria-pressed={thumbs === 'up'} aria-label="This eval got it right">
          <ThumbIcon direction="up" filled={thumbs === 'up'} size={13} />
        </button>
        <button type="button" disabled={busy} onClick={clickDown} className={btn(thumbs === 'down')}
          title={thumbs === 'down' ? 'Marked wrong. Click to edit' : 'Wrong'}
          aria-pressed={thumbs === 'down'} aria-expanded={open} aria-label="This eval got it wrong">
          <ThumbIcon direction="down" filled={thumbs === 'down'} size={13} />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-64 space-y-2 rounded-md border border-[color:var(--border-default)] bg-surface p-2 text-[11px] shadow-lg" onClick={stop} role="presentation">
          <div className="font-medium text-[color:var(--text-primary)]">Should have been</div>
          <div className="flex gap-1">
            {[['Pass', true], ['Fail', false]].map(([label, v]) => (
              <button key={label} type="button" onClick={(e) => { stop(e); setCorrectedPassed(v); }}
                className={`flex-1 rounded border px-2 py-1 ${correctedPassed === v
                  ? 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]'
                  : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}>
                {label}
              </button>
            ))}
          </div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={4000} aria-label="Why was it wrong"
            placeholder="Why?"
            className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5 text-[11px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]" />
          {error && <div className="text-[color:var(--feedback-error)]">{error}</div>}
          <div className="flex items-center gap-1">
            <button type="button" disabled={busy} onClick={saveDown} className="rounded bg-brand px-2 py-1 font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={(e) => { stop(e); setOpen(false); }} className="rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
            {value?.id && thumbs === 'down' && (
              <button type="button" disabled={busy} onClick={(e) => { stop(e); retract(); }} className="ml-auto rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Remove</button>
            )}
          </div>
        </div>
      )}
      {!open && error && <div className="mt-0.5 text-[10px] text-[color:var(--feedback-error)]">{error}</div>}
    </div>
  );
}
